import * as AuthActions from '../actions/auth.actions';
import * as principalsActions from '../actions/principals.actions';
import * as userObjectActions from '../actions/core.actions';
import * as widgetActions from '../actions/widget.actions';

import { Action, Store, select } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Dashboard, DashboardPage, User } from '../models/core.models';
import { EMPTY, from, of, race, timer } from 'rxjs';
import { ObjectService, Principal } from '../services/object.service';
import {
  UserContextObjectProperty,
  UserService,
} from '../services/user.service';
import {
  ViewId,
  ViewPreset,
  ViewPresetService,
} from '../../shared/services/view-preset.service';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import {
  createDashboardPage,
  createDashboardPageFail,
  createDashboardPageSuccess,
  deleteDashboardPage,
  deleteDashboardPageFail,
  deleteDashboardPageSuccess,
  fetchUserObjects,
  fetchUserObjectsByIds,
  fetchUserObjectsByIdsFail,
  fetchUserObjectsByIdsSuccess,
  fetchUserObjectsFail,
  fetchUserObjectsSuccess,
  fetchWidgetSettings,
  fetchWidgetSettingsFail,
  fetchWidgetSettingsSuccess,
  loadDashboardUIState,
  loadDashboardUIStateFail,
  loadDashboardUIStateSuccess,
  loadDashboardViewPreset,
  loadDashboardViewPresetFail,
  loadDashboardViewPresetSuccess,
  reorderDashboardPages,
  reorderDashboardPagesFail,
  reorderDashboardPagesSuccess,
  saveDashboardUIState,
  saveDashboardUIStateFail,
  saveDashboardUIStateSuccess,
  saveDashboardViewPreset,
  saveDashboardViewPresetFail,
  saveDashboardViewPresetSuccess,
  selectDashboardPage,
  toggleObjectEditMode,
  toggleWidgetEditMode,
  toggleWidgetLanguageOption,
  toggleWidgetLanguageOptionFail,
  toggleWidgetLanguageOptionSuccess,
  updateDashboardPage,
  updateDashboardPageFail,
  updateDashboardPagePeriod,
  updateDashboardPageSuccess,
  updateDashboardUIState,
} from '../actions/object.actions';
import {
  editUser,
  editUserFail,
  editUserSuccess,
} from 'src/app/admin/actions/admin-users.actions';
import {
  fetchHomeViewConfig,
  fetchHomeViewConfigFail,
  fetchHomeViewConfigSuccess,
  setHomeObjectsLifeCycle,
  toggleHomeViewConfigOption,
  toggleHomeViewConfigOptionFail,
  toggleHomeViewConfigOptionSuccess,
} from '../actions/home.actions';
import {
  fetchWorkspaceViewConfig,
  fetchWorkspaceViewConfigFail,
  fetchWorkspaceViewConfigSuccess,
  setWorkspaceObjectsLifeCycle,
  toggleWorkspaceViewConfigOption,
  toggleWorkspaceViewConfigOptionFail,
  toggleWorkspaceViewConfigOptionSuccess,
} from '../actions/workspace.actions';
import {
  getContentFilters,
  selectCoreAppSection,
  selectCoreAvailableModules,
  selectCoreDarkMode,
  selectCurrentObject,
  selectDashboardPages,
  selectHomeObjectsLifeCycle,
  selectHomeViewOptions,
  selectLoadedPageIds,
  selectMainMenuExpanded,
  selectPageLoadingStates,
  selectPrincipals,
  selectSelectedPageId,
  selectUser,
  selectUserAccessLevel,
  selectWidgetEditMode,
  selectWidgetLanugageConfig,
  selectWidgets,
  selectWorkspaceViewConfig,
  selectWorspaceObjectsLifeCycle,
} from '../reducers';
import {
  sendForgottenPasswordEmailFail,
  sendForgottenPasswordEmailSuccess,
  updateUserObjectContextSettings,
  updateUserObjectContextSettingsFailure,
  updateUserObjectContextSettingsSuccess,
} from '../actions/user.actions';
import { submitFormFailure, submitFormSuccess } from '../actions/form.actions';

import { AppState } from 'src/app/reducers';
import { CoreService } from '../services/core.service';
import { EditorFormEntity } from '../models/form.models';
import { HomeSettingsService } from '../services/home-settings.service';
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NgToastService } from 'ng-angular-popup';
import { NomObjectClass, WidgetType } from '../constants';
import { Router } from '@angular/router';
import { ToastService } from '@services/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { UpdateDashboardPageParams } from '../models/widget.models';
import { WidgetService } from '../services/widget.service';
import { WidgetSettingsService } from '../services/widget-settings.service';
import { WorkspaceSettingsService } from '../services/workspace-settings.service';
import { concatLatestFrom } from '@ngrx/operators';
import { sendForgottenPasswordEmail } from '../actions/user.actions';
import { toggleMainMenuExpanded } from '../actions/main-menu.actions';
import { language } from '@codemirror/language';
import { PropertyViewConfig } from 'src/app/core/services/feed-settings.service';
import { toggleWidgetEditModeSuccess } from '../actions/object.actions';
import { toggleWidgetEditModeFail } from '../actions/object.actions';

@Injectable()
export class CoreEffects {
  fetchUserWorkspaces$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        userObjectActions.fetchUserWorkspaces,
        AuthActions.switchUserCompanySuccess,
        setHomeObjectsLifeCycle,
      ),
      mergeMap((action) =>
        race(
          this.store.select(selectHomeViewOptions).pipe(
            filter((viewOptions) => !!viewOptions && viewOptions.length > 0),
            take(1),
          ),
          timer(2000).pipe(map(() => null)),
        ).pipe(
          take(1),
          withLatestFrom(
            this.store.select(selectUser),
            this.store.select(selectHomeObjectsLifeCycle),
          ),
          mergeMap(([viewOptions, user, homeObjectsLifeCycle]) => {
            const withStats =
              viewOptions?.find((opt) => opt.path === 'stats')?.selected ??
              false;
            return this.coreService
              .fetchUserWorkspaces(user.id, [homeObjectsLifeCycle], withStats)
              .pipe(
                map((workspaces) =>
                  userObjectActions.fetchUserWorkspacesSuccess({ workspaces }),
                ),
                catchError((error) =>
                  of(userObjectActions.fetchUserWorkspacesFail({ error })),
                ),
              );
          }),
        ),
      ),
    ),
  );

  fetchWorkspaceObjects$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        userObjectActions.fetchWorkspaceObjects,
        setWorkspaceObjectsLifeCycle,
      ),
      withLatestFrom(this.store.select(selectWorspaceObjectsLifeCycle)),
      switchMap(([action, workspaceObjectsLifeCycle]) =>
        this.coreService
          .fetchUserWorkspaceObjects(action.workspaceId, [
            workspaceObjectsLifeCycle,
          ])
          .pipe(
            map((objects) =>
              userObjectActions.fetchWorkspaceObjectsSuccess({ objects }),
            ),
            catchError((error) =>
              of(userObjectActions.fetchWorkspaceObjectsFail({ error })),
            ),
          ),
      ),
    ),
  );

  listCompanyObjectsSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.listCompanyObjectsSuccess),
      // or withLatestFrom(this.store.select(selectUser)) – both work
      concatLatestFrom(() => this.store.select(selectUser)),
      mergeMap(([action, user]) => {
        // If there is no user yet, do nothing.
        if (!user) {
          return EMPTY;
        }
        const actionsToDispatch: Action[] = [];
        // Always fetch workspaces and recent objects
        actionsToDispatch.push(
          userObjectActions.fetchUserWorkspaces({ userId: user.id }),
          userObjectActions.loadUserRecentObjects(),
        );
        // Emit all actions
        return of(...actionsToDispatch);
      }),
    ),
  );

  triggerLoadUserObjects$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loginSuccess, AuthActions.remoteLoginSuccess),

      mergeMap(({ response }) => {
        // Correctly destructure to get response from the action payload

        localStorage.setItem(
          'darkMode',
          JSON.stringify(response.user.dark_mode),
        );
        return [
          userObjectActions.fetchUserWorkspaces({ userId: response.id }),
          // userObjectActions.loadUserObjects({
          //   lifeCycleStatus: [NomObjectLifecycleStatus.ACTIVE],
          // }),
          userObjectActions.loadUserRecentObjects(),
        ];
      }),
    ),
  );

  toggleDarkMode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userObjectActions.toggleDarkMode),
      withLatestFrom(
        this.store.select(selectCoreDarkMode),
        this.store.select(selectUser),
      ),
      switchMap(([, darkMode, user]) => {
        //  Always store in localStorage

        localStorage.setItem('darkMode', JSON.stringify(darkMode));

        if (!user) {
          // No user: just update state locally
          return of(userObjectActions.toggleDarkModeSuccess({ darkMode }));
        }

        // User present: persist remotely as well
        return this.userService.setDarkMode(darkMode).pipe(
          map(() => userObjectActions.toggleDarkModeSuccess({ darkMode })),
          catchError((error) =>
            of(userObjectActions.toggleDarkModeFail({ error })),
          ),
        );
      }),
    ),
  );

  toggleMainMenu$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(toggleMainMenuExpanded),
        withLatestFrom(this.store.select(selectMainMenuExpanded)),
        switchMap(([action, expanded]) => {
          return this.userService.setMenuStateOpen(expanded);
        }),
      ),
    { dispatch: false },
  );

  createWorkspace$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userObjectActions.createWorkspace),
      mergeMap((action) =>
        this.coreService.createWorkspace(action.name, action.description).pipe(
          switchMap((workspace) => [
            userObjectActions.createWorkspaceSuccess({ workspace }), // Entity-specific success action
            submitFormSuccess({
              entity: EditorFormEntity.Workspace,
              object: workspace,
              isCreated: true,
            }), // Generic form success action
          ]),
          catchError((error) =>
            of(
              userObjectActions.createWorkspaceFail({ error }), // Entity-specific failure action
              submitFormFailure({ entity: EditorFormEntity.Workspace, error }), // Generic form failure action
            ),
          ),
        ),
      ),
    ),
  );

  createWorkspaceSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(userObjectActions.createWorkspaceSuccess),
        tap(() => {
          this.toastService.showSuccess(
            this.translateService.instant(
              'workspace-editor-popup.msg.workspace-created-successfully',
            ),
          );
        }),
      ),
    { dispatch: false },
  );

  editWorkspace$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userObjectActions.updateWorkspace),
      mergeMap((action) =>
        this.coreService.updateWorkspace(action.id, action.workspace).pipe(
          switchMap((workspace) => [
            userObjectActions.updateWorkspaceSuccess({ workspace }), // Entity-specific success action
            submitFormSuccess({
              entity: EditorFormEntity.Workspace,
              object: workspace,
              isCreated: false,
            }), // Generic form success action
          ]),
          catchError((error) =>
            of(
              userObjectActions.updateWorkspaceFail({ error }), // Entity-specific failure action
              submitFormFailure({ entity: EditorFormEntity.Workspace, error }), // Generic form failure action
            ),
          ),
        ),
      ),
    ),
  );

  editWorkspaceSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(userObjectActions.updateWorkspaceSuccess),
        tap(() => {
          this.toastService.showSuccess(
            this.translateService.instant(
              'workspace-editor-popup.msg.workspace-updated-successfully',
            ),
          );
        }),
      ),
    { dispatch: false },
  );

  deleteWorkspace$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userObjectActions.deleteWorkspace),
      mergeMap((action) =>
        this.coreService.deleteWorkspace(action.workspaceId).pipe(
          map((workspaceId) =>
            userObjectActions.deleteWorkspaceSuccess({ workspaceId }),
          ),
          catchError((error) =>
            of(userObjectActions.deleteWorkspaceFail({ error })),
          ),
        ),
      ),
    ),
  );

  createDashboard$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userObjectActions.createDashboard),
      mergeMap((action) =>
        this.coreService.createDashboard(action.dashboard).pipe(
          switchMap((dashboard) => [
            userObjectActions.createDashboardSuccess({ dashboard }), // Entity-specific success action
            submitFormSuccess({
              entity: EditorFormEntity.Dashboard,
              object: dashboard,
              isCreated: true,
            }), // Generic form success action
          ]),
          catchError((error) =>
            of(
              userObjectActions.createDashboardFail({ error }), // Entity-specific failure action
              submitFormFailure({ entity: EditorFormEntity.Dashboard, error }), // Generic form failure action
            ),
          ),
        ),
      ),
    ),
  );

  createDashboardSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(userObjectActions.createDashboardSuccess),
        tap(() => {
          this.toastService.showSuccess(
            this.translateService.instant(
              'dashboard-editor-popup.msg.dashboard-created-successfully',
            ),
          );
        }),
      ),
    { dispatch: false },
  );

  editDashboard$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userObjectActions.updateDashboard),
      mergeMap((action) =>
        this.coreService.updateDashboard(action.id, action.dashboard).pipe(
          switchMap((dashboard) => [
            userObjectActions.updateDashboardSuccess({ dashboard }), // Entity-specific success action
            submitFormSuccess({
              entity: EditorFormEntity.Dashboard,
              object: dashboard,
              isCreated: false,
            }), // Generic form success action
          ]),
          catchError((error) =>
            of(
              userObjectActions.updateDashboardFail({ error }), // Entity-specific failure action
              submitFormFailure({ entity: EditorFormEntity.Dashboard, error }), // Generic form failure action
            ),
          ),
        ),
      ),
    ),
  );

  editDashboardSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(userObjectActions.updateDashboardSuccess),
        tap((action) => {
          this.toastService.showSuccess(
            this.translateService.instant(
              'dashboard-editor.msg.dashboard-updated-successfully',
            ),
          );
        }),
      ),
    { dispatch: false },
  );

  deleteDashboard$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userObjectActions.deleteDashboard),
      mergeMap((action) =>
        this.coreService.deleteDashboard(action.dashboardId).pipe(
          map((dashboardId) =>
            userObjectActions.deleteDashboardSuccess({ dashboardId }),
          ),
          catchError((error) =>
            of(userObjectActions.deleteDashboardFail({ error })),
          ),
        ),
      ),
    ),
  );

  // ! Multi-Page Dashboard actions - Start
  // * add toaster success/error behavior
  createDashboardPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createDashboardPage),
      mergeMap((action) =>
        this.widgetService
          .createDashboardPage({
            dashboardId: action.dashboardId,
          })
          .pipe(
            map((page) => createDashboardPageSuccess({ page })),
            catchError((error) => of(createDashboardPageFail({ error }))),
          ),
      ),
    ),
  );

  deleteDashboardPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteDashboardPage),
      withLatestFrom(this.store.select(selectDashboardPages)),
      filter(([action, pages]) => {
        // * Prevent deletion of last page - must have at least one page
        return pages && pages.length > 1;
      }),
      mergeMap(([action, pages]) =>
        this.widgetService
          .deleteDashboardPage(action.dashboardId, action.pageId)
          .pipe(
            switchMap((updatedPages) => [
              // * 1. Update dashboard pages and handle page selection
              deleteDashboardPageSuccess({
                pages: updatedPages,
                deletedPageId: action.pageId,
                // * Pass original pages for selection logic
                originalPages: pages,
              }),
              // * 2. Clean up widgets for deleted page
              widgetActions.deleteWidgetsByPageId({ pageId: action.pageId }),
            ]),
            catchError((error) => of(deleteDashboardPageFail({ error }))),
          ),
      ),
    ),
  );

  updateDashboardPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateDashboardPage),
      mergeMap((action) =>
        this.widgetService
          .updateDashboardPage(action.page as UpdateDashboardPageParams)
          .pipe(
            map((page) => updateDashboardPageSuccess({ page })),
            catchError((error) => of(updateDashboardPageFail({ error }))),
          ),
      ),
    ),
  );

  reorderDashboardPages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(reorderDashboardPages),
      mergeMap((action) =>
        this.widgetService
          .reorderDashboardPages({
            dashboardId: action.dashboardId,
            orderedPageIds: action.orderedIds,
          })
          .pipe(
            switchMap((response) => {
              if (response?.success) {
                return of(
                  reorderDashboardPagesSuccess({ pages: action.orderedPages }),
                );
              } else {
                return of(
                  reorderDashboardPagesFail({
                    error: 'Server returned success: false',
                    rollbackPages: action.rollbackPages,
                  }),
                );
              }
            }),
            catchError((error) =>
              of(
                reorderDashboardPagesFail({
                  error: error.message || 'Failed to reorder pages',
                  rollbackPages: action.rollbackPages,
                }),
              ),
            ),
          ),
      ),
    ),
  );

  // * Page Period updates - calls API to persist period changes
  updateDashboardPagePeriod$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateDashboardPagePeriod),
      withLatestFrom(this.store.select(selectCurrentObject)),
      filter(
        ([action, currentObject]) =>
          !!currentObject &&
          currentObject.object_class_id === NomObjectClass.DASHBOARD,
      ),
      mergeMap(([action, currentObject]) => {
        const updateParams: UpdateDashboardPageParams = {
          dashboard_id: currentObject.id,
          page_id: action.pageId,
          period: action.period,
        };

        return this.widgetService.updateDashboardPage(updateParams).pipe(
          map((page) => updateDashboardPageSuccess({ page })),
          catchError((error) => {
            return of(updateDashboardPageFail({ error: error.message }));
          }),
        );
      }),
    ),
  );

  selectDashboardPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(selectDashboardPage, deleteDashboardPageSuccess),
      withLatestFrom(
        this.store.select(selectCurrentObject),
        this.store.select(selectLoadedPageIds),
        this.store.select(selectPageLoadingStates),
        this.store.select(selectSelectedPageId),
      ),
      map(
        ([
          action,
          currentObject,
          loadedPageIds,
          pageLoadingStates,
          selectedPageId,
        ]) => {
          // ! extract pageId from different action types
          let pageId: number;
          if (action.type === '[Object] Select Dashboard Page') {
            pageId = (action as any).pageId;
          } else if (action.type === '[Object] Delete Dashboard Page Success') {
            // ! Use the newly selected page from state
            pageId = selectedPageId;
          }

          return { pageId, currentObject, loadedPageIds, pageLoadingStates };
        },
      ),
      filter(
        ({ pageId, currentObject, loadedPageIds, pageLoadingStates }) =>
          !!pageId &&
          !!currentObject &&
          currentObject.object_class_id === NomObjectClass.DASHBOARD &&
          !loadedPageIds.has(pageId) && // Skip if already loaded
          !pageLoadingStates.get(pageId), // Skip if currently loading
      ),
      mergeMap(({ pageId, currentObject }) =>
        of(
          widgetActions.loadWidgets({
            objectId: currentObject.id,
            objectClassId: currentObject.object_class_id,
            pageId: pageId,
          }),
        ),
      ),
    ),
  );

  // * Dashboard View Preset effects - Start
  loadDashboardViewPreset$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadDashboardViewPreset),
      mergeMap((action) =>
        this.viewPresetService
          .getViewPresetByContext(
            action.dashboardId,
            NomObjectClass.DASHBOARD,
            ViewId.ObjectViewDashboardPage,
          )
          .pipe(
            map((viewPreset) => {
              const settings = viewPreset?.settings || {};
              return loadDashboardViewPresetSuccess({
                pageId: settings.active_page_id,
              });
            }),
            catchError((error) => {
              this.toastService.showError(
                this.translateService.instant(
                  'dashboard-presentation-preview-mode.message.failed-to-load-view-preset',
                ),
              );

              return of(
                loadDashboardViewPresetFail({
                  error: error.message,
                }),
              );
            }),
          ),
      ),
    ),
  );

  saveDashboardViewPreset$ = createEffect(() =>
    this.actions$.pipe(
      ofType(saveDashboardViewPreset),
      mergeMap((action) => {
        const viewPreset = {
          view_id: ViewId.ObjectViewDashboardPage,
          context_object_id: action.dashboardId,
          context_object_class_id: NomObjectClass.DASHBOARD,
          name: '',
          settings: {
            active_page_id: action.pageId,
          },
        };

        return this.viewPresetService.saveViewPreset(viewPreset, false).pipe(
          map(() => saveDashboardViewPresetSuccess()),
          catchError((error) => {
            this.toastService.showError(
              this.translateService.instant(
                'dashboard-presentation-preview-mode.message.failed-to-save-view-preset',
              ),
            );

            return of(
              saveDashboardViewPresetFail({
                error: error.message,
              }),
            );
          }),
        );
      }),
    ),
  );
  // * Dashboard View Preset effects - End

  // * Dashboard UI State effects - Start
  loadDashboardUIState$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadDashboardUIState),
      mergeMap((action) =>
        this.viewPresetService
          .getViewPresetByContext(
            action.dashboardId,
            NomObjectClass.DASHBOARD,
            ViewId.ObjectViewDashboard,
          )
          .pipe(
            map((viewPreset) => {
              const settings = viewPreset?.settings || {};
              return loadDashboardUIStateSuccess({
                viewPreset: {
                  pagination_menu_open: settings.pagination_menu_open ?? false,
                },
              });
            }),
            catchError((error) => {
              return of(
                loadDashboardUIStateSuccess({
                  viewPreset: {
                    pagination_menu_open: false,
                  },
                }),
              );
            }),
          ),
      ),
    ),
  );

  updateDashboardUIState$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateDashboardUIState),
      withLatestFrom(this.store.select(selectCurrentObject)),
      map(([action, currentObject]) => {
        const dashboard = currentObject as Dashboard;
        const currentViewPreset = dashboard?.view_preset || {
          pagination_menu_open: false,
        };

        return saveDashboardUIState({
          dashboardId: action.dashboardId,
          viewPreset: {
            ...currentViewPreset,
            ...action.updates,
          },
        });
      }),
    ),
  );

  saveDashboardUIState$ = createEffect(() =>
    this.actions$.pipe(
      ofType(saveDashboardUIState),
      mergeMap((action) => {
        const viewPreset = {
          view_id: ViewId.ObjectViewDashboard,
          context_object_id: action.dashboardId,
          context_object_class_id: NomObjectClass.DASHBOARD,
          name: '',
          settings: action.viewPreset,
        };

        return this.viewPresetService.saveViewPreset(viewPreset, false).pipe(
          map(() => saveDashboardUIStateSuccess()),
          catchError((error) => {
            return of(saveDashboardUIStateSuccess());
          }),
        );
      }),
    ),
  );
  // * Dashboard UI State effects - End

  // ! Multi-Page Dashboard actions - End

  // loadCompanyPrincipals$ = createEffect(() =>
  //   this.actions$.pipe(
  //     ofType(principalsActions.loadCompanyPrincipals),
  //     switchMap((action) =>
  //       this.coreService
  //         .fetchCompanyPrincipals(
  //           action.companyId,
  //           action.page,
  //           action.query,
  //           action.objectClassId,
  //         )
  //         .pipe(
  //           map((response) =>
  //             principalsActions.loadCompanyPrincipalsSuccess({
  //               principals: response.items,
  //               totalItems: response.total,
  //               page: action.page,
  //             }),
  //           ),
  //           catchError((error) =>
  //             of(principalsActions.loadCompanyPrincipalsFail({ error })),
  //           ),
  //         ),
  //     ),
  //   ),
  // );

  loadMissingPrincipals$ = createEffect(() =>
    this.actions$.pipe(
      ofType(principalsActions.loadMissingPrincipals),
      switchMap(({ ids, companyId }) =>
        this.coreService
          .getPrincipalsByCompanyIncludingIds(companyId, ids)
          .pipe(
            map(
              (response) =>
                principalsActions.initializeSelectedPrincipals({
                  principals: response.items,
                }), // Reuse existing action
            ),
            catchError(() => EMPTY),
          ),
      ),
    ),
  );

  massObjectOperation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userObjectActions.massObjectOperation),
      mergeMap((action) =>
        this.coreService
          .massObjectOperation(action.operation, action.objects)
          .pipe(
            map((response) =>
              userObjectActions.massObjectOperationSuccess({ response }),
            ),
            catchError((error) =>
              of(userObjectActions.massObjectOperationFail({ error })),
            ),
          ),
      ),
    ),
  );

  updateRecentObject$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(userObjectActions.objectChanged),
        filter((action) => !!action.object), // Ensure action.object is not null or undefined
        mergeMap((action) =>
          this.userService
            .addRecentObject({
              object_id: action.object.id,
              object_class_id: action.object.object_class_id,
              object_type_id: action.object.object_type_id,
            })
            .pipe(
              catchError((error) => {
                // Handle the error or log it
                console.error('Error adding recent object', error);
                return of({}); // Return an observable of an empty object or a more appropriate fallback action
              }),
            ),
        ),
      ),
    { dispatch: false },
  );

  loadUserRecentObjects = createEffect(() =>
    this.actions$.pipe(
      ofType(userObjectActions.loadUserRecentObjects),
      withLatestFrom(this.store.select(selectUser)),
      mergeMap(([action, user]) =>
        this.userService.fetchRecentObjects().pipe(
          map((recentObjects) =>
            userObjectActions.loadUserRecentObjectsSuccess({ recentObjects }),
          ),
          catchError((error) =>
            of(userObjectActions.loadUserRecentObjectsFail({ error })),
          ),
        ),
      ),
    ),
  );

  massOperationSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(userObjectActions.massObjectOperationSuccess),
        tap((action) => {
          const operation = action.response.operation;
          const message = `Operation ${operation} was successful`;

          this.toastService.showSuccess(message);
        }),
      ),
    { dispatch: false },
  );

  massOperationFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(userObjectActions.massObjectOperationFail),
        tap((action) => {
          const message = action.error;

          this.toastService.showError(message);
        }),
      ),
    { dispatch: false },
  );

  saveObjectPermissions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userObjectActions.saveObjectPermissions),
      mergeMap((action) =>
        this.objectService
          .updateObjectsPermissions(action.objects, action.permissions)
          .pipe(
            map((response) =>
              userObjectActions.saveObjectPermissionsSuccess({ response }),
            ),
            catchError((error) =>
              of(userObjectActions.saveObjectPermissionsFail({ error })),
            ),
          ),
      ),
    ),
  );

  saveObjectPermissionSuccess = createEffect(
    () =>
      this.actions$.pipe(
        ofType(userObjectActions.saveObjectPermissionsSuccess),
        tap(() => {
          this.toastService.showSuccess(
            this.translateService.instant(
              'object-permissions.msg.premissions-saved-successfully',
            ),
          );
        }),
      ),
    { dispatch: false },
  );

  moveObjects$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userObjectActions.moveObjects),
      switchMap((action) => {
        const objectList = action.objects.map((object) => ({
          object_id: object.id,
          object_class_id: object.object_class_id,
          object_type_id: object.object_type_id,
        }));

        return this.coreService
          .moveObjects(objectList, action.workspace_id)
          .pipe(
            map((objects) => userObjectActions.moveObjectsSuccess({ objects })),
            catchError((error) =>
              of(userObjectActions.moveObjectsFail({ error })),
            ),
          );
      }),
    ),
  );

  fetchUserObjects$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fetchUserObjects),
      switchMap((action) =>
        this.userService
          .fetchUserObjects(
            action.query,
            action.workspaceId,
            action.objectClassIds,
            action.objectTypeIds,
            true,
            action.sortField,
            action.sortDirection,
          )
          .pipe(
            map((filteredObjects) =>
              fetchUserObjectsSuccess({ objects: filteredObjects }),
            ),
            catchError((error) => of(fetchUserObjectsFail({ error }))),
          ),
      ),
    ),
  );

  fetchUserObjectsByIds$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fetchUserObjectsByIds),
      switchMap((action) =>
        this.userService.fetchUserObjectsByIds(action.objectIds).pipe(
          map((objects) => fetchUserObjectsByIdsSuccess({ objects })),
          catchError((error) => of(fetchUserObjectsByIdsFail({ error }))),
        ),
      ),
    ),
  );

  sendEmail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userObjectActions.sendEmail),
      withLatestFrom(this.store.select(getContentFilters)),
      take(1),
      switchMap(([action, contentFilters]) =>
        this.coreService.email(action.params, contentFilters).pipe(
          switchMap((response) => [
            userObjectActions.sendEmailSuccess({ response }), // Entity-specific success action
            submitFormSuccess({
              entity: EditorFormEntity.ExportForm,
              object: response,
              isCreated: false,
            }), // Generic form success action
          ]),
          catchError((error) =>
            of(
              userObjectActions.sendEmailFail({ error }), // Entity-specific failure action
              submitFormFailure({ entity: EditorFormEntity.ExportForm, error }), // Generic form failure action
            ),
          ),
        ),
      ),
    ),
  );

  toggleObjectEditMode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(toggleObjectEditMode),
      withLatestFrom(this.store.pipe(select(selectCurrentObject))),
      mergeMap(([, currentObject]) => {
        if (!currentObject) {
          return of(
            updateUserObjectContextSettingsFailure({
              error: 'No current object selected',
            }),
          );
        }
        const { object_id: objectId, object_class_id: objectClassId } =
          currentObject; // Ensure these are defined in your state
        return this.userService
          .updateObjectPropertyValue(
            objectId,
            objectClassId,
            UserContextObjectProperty.EditMode, // Assuming 'edit_mode' is a correct property key
            currentObject?.['user_settings']?.edit_mode,
          )
          .pipe(
            map((userObjectContextSettings) =>
              updateUserObjectContextSettingsSuccess({
                userObjectContextSettings,
              }),
            ),
            catchError((error) =>
              of(updateUserObjectContextSettingsFailure({ error })),
            ),
          );
      }),
    ),
  );

  updateUserObjectContextSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateUserObjectContextSettings),
      mergeMap((action) =>
        this.userService
          .updateObjectPropertyValue(
            action.objectId,
            action.objectClassId,
            action.propertyKey,
            action.propertyValue,
          )
          .pipe(
            map((userObjectContextSettings) =>
              updateUserObjectContextSettingsSuccess({
                userObjectContextSettings,
              }),
            ),
            catchError((error) =>
              of(updateUserObjectContextSettingsFailure({ error })),
            ),
          ),
      ),
    ),
  );

  editUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(editUser),
      switchMap((action) =>
        this.userService.manageUser(action.userData).pipe(
          switchMap((user) => [
            editUserSuccess({ userData: user }), // Entity-specific success action
            submitFormSuccess({
              entity: EditorFormEntity.User,
              object: user,
              isCreated: action.userData.id !== undefined,
            }), // Generic form submission success action
          ]),
          catchError((error: HttpErrorResponse) => {
            const errorMessage =
              error?.error?.body?.message || 'An unexpected error occurred';

            return of(
              editUserFail({ error: errorMessage }), // Entity-specific failure action
              submitFormFailure({
                entity: EditorFormEntity.User,
                error: errorMessage,
              }), // Generic form submission failure action
            );
          }),
        ),
      ),
    ),
  );

  editUserSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(editUserSuccess),
        tap(() => {
          this.toastService.showSuccess(
            this.translateService.instant(
              'user-popup.msg.user-settings-updated-successfully',
            ),
          );
        }),
      ),
    { dispatch: false },
  );

  editUserFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(editUserFail),
        tap(() => {
          this.toastService.showError(
            this.translateService.instant(
              'user-popup.msg.user-settings-update-failed',
            ),
          );
        }),
      ),
    { dispatch: false },
  );

  sendForgottenPasswordEmail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sendForgottenPasswordEmail),
      switchMap((action) =>
        this.userService
          .sendForgottenPasswordEmail(action.email, action.resetType)
          .pipe(
            map(() => sendForgottenPasswordEmailSuccess()),
            catchError((error) => of(sendForgottenPasswordEmailFail)),
          ),
      ),
    ),
  );

  sendForgottenPasswordEmailSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(sendForgottenPasswordEmailSuccess),
        tap(() => {
          this.toastService.showSuccess(
            this.translateService.instant(
              'user-popup.msg.reset-user-password-email-sent',
            ),
          );
        }),
      ),
    { dispatch: false },
  );

  sendForgottenPasswordEmailFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(sendForgottenPasswordEmailFail),
        tap(() => {
          this.toastService.showError(
            this.translateService.instant(
              'user-popup.msg.sending-reset-user-password-email-failed',
            ),
          );
        }),
      ),
    { dispatch: false },
  );

  switchUserCompanySuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.switchUserCompanySuccess),
      map(() => userObjectActions.loadUserRecentObjects()),
    ),
  );

  switchUserCompanySuccessRedirect$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.switchUserCompanySuccess),
        withLatestFrom(
          this.store.select(selectUserAccessLevel),
          this.store.select(selectCoreAvailableModules),
          this.store.select(selectCoreAppSection),
        ),
        tap(([action, userAccessLevel, availableModules, appSection]) => {
          const currentModule = availableModules.find(
            (module) => module.name.toLowerCase() === appSection,
          );

          if (currentModule && userAccessLevel >= currentModule.accessLevel) {
            this.router.navigate([currentModule.url]);
          } else {
            this.router.navigate(['/analytics']);
          }
        }),
      ),
    { dispatch: false },
  );

  fetchHomeViewConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fetchHomeViewConfig),
      withLatestFrom(this.store.select(selectUser)),
      switchMap(([action, user]) =>
        this.viewPresetService
          .getViewPresetByContext(
            action.userId,
            NomObjectClass.USER,
            ViewId.HomeViewObjects,
          )
          .pipe(
            switchMap((viewPreset) =>
              from(this.homeSettings.getHomeViewConfig(user)).pipe(
                map((defaultHomeConfig) => {
                  const isSettingsEmpty =
                    !viewPreset.settings ||
                    (typeof viewPreset.settings === 'object' &&
                      Object.keys(viewPreset.settings).length === 0);

                  const homeConfig = isSettingsEmpty
                    ? defaultHomeConfig
                    : viewPreset.settings;

                  return fetchHomeViewConfigSuccess({
                    defaultHomeConfig,
                    homeConfig,
                  });
                }),
              ),
            ),
            catchError((error) => of(fetchHomeViewConfigFail({ error }))),
          ),
      ),
    ),
  );

  toggleHomeViewConfiguration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(toggleHomeViewConfigOption),
      withLatestFrom(
        this.store.select(selectHomeViewOptions),
        this.store.select(selectUser),
      ),
      switchMap(([action, viewConfig, user]) => {
        const viewPreset: ViewPreset = {
          view_id: ViewId.HomeViewObjects,
          name: '', // or use action.name if it's a "Save As" flow
          settings: viewConfig,
          context_object_id: user.id,
          context_object_class_id: NomObjectClass.USER,
        };

        viewPreset.settings = viewPreset.settings.map((configItem) => {
          return configItem;
        });

        return this.viewPresetService.saveViewPreset(viewPreset, false).pipe(
          map((savedPreset) => toggleHomeViewConfigOptionSuccess()),
          catchError((error) => of(toggleHomeViewConfigOptionFail({ error }))),
        );
      }),
    ),
  );

  refetchWorkspacesOnStatsEnabled$ = createEffect(() =>
    this.actions$.pipe(
      ofType(toggleHomeViewConfigOption),
      withLatestFrom(
        this.store.select(selectHomeViewOptions),
        this.store.select(selectUser),
      ),
      filter(([action, viewOptions, user]) => {
        const stats = viewOptions?.find((opt) => opt.path === 'stats');
        return (
          !!user && stats?.label === action.optionName && !!stats?.selected
        );
      }),
      map(([, , user]) =>
        userObjectActions.fetchUserWorkspaces({ userId: user.id }),
      ),
    ),
  );

  fetchWorkspaceViewConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fetchWorkspaceViewConfig),
      withLatestFrom(this.store.select(selectUser)),
      switchMap(([action, user]) =>
        this.viewPresetService
          .getViewPresetByContext(
            action.userId,
            NomObjectClass.USER,
            ViewId.WorkspaceViewObjects,
          )
          .pipe(
            switchMap((viewPreset) =>
              from(this.workspaceSettings.getWorkspaceViewConfig(user)).pipe(
                map((defaultWorkspaceConfig) => {
                  const isSettingsEmpty =
                    !viewPreset.settings ||
                    (typeof viewPreset.settings === 'object' &&
                      Object.keys(viewPreset.settings).length === 0);

                  const workspaceConfig = isSettingsEmpty
                    ? defaultWorkspaceConfig
                    : viewPreset.settings;

                  return fetchWorkspaceViewConfigSuccess({
                    defaultWorkspaceConfig,
                    workspaceConfig,
                  });
                }),
              ),
            ),
            catchError((error) => of(fetchWorkspaceViewConfigFail({ error }))),
          ),
      ),
    ),
  );

  toggleWorkspaceViewConfiguration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(toggleWorkspaceViewConfigOption),
      withLatestFrom(
        this.store.select(selectWorkspaceViewConfig),
        this.store.select(selectUser),
      ),
      switchMap(([action, viewConfig, user]) => {
        const viewPreset: ViewPreset = {
          view_id: ViewId.WorkspaceViewObjects,
          name: '', // or use action.name if it's a "Save As" flow
          settings: viewConfig,
          context_object_id: user.id,
          context_object_class_id: NomObjectClass.USER,
        };

        viewPreset.settings = viewPreset.settings.map((configItem) => {
          return configItem;
        });

        return this.viewPresetService.saveViewPreset(viewPreset, false).pipe(
          map((savedPreset) => toggleWorkspaceViewConfigOptionSuccess()),
          catchError((error) =>
            of(toggleWorkspaceViewConfigOptionFail({ error })),
          ),
        );
      }),
    ),
  );

  triggerWidgetSettingsLoad$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userObjectActions.objectChanged),
      filter(
        (action) =>
          !!action.object &&
          action.object.object_class_id !== NomObjectClass.WORKSPACE,
      ),
      map((action) =>
        fetchWidgetSettings({
          objectId: action.object.id,
          objectClassId: action.object.object_class_id,
        }),
      ),
    ),
  );

  loadWidgetSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fetchWidgetSettings),
      withLatestFrom(this.store.select(selectUser)),
      switchMap(([action, user]) =>
        this.viewPresetService
          .getViewPresetByContext(
            action.objectId,
            action.objectClassId,
            ViewId.PanelViewWidgets,
          )
          .pipe(
            switchMap((viewPreset) =>
              from(this.widgetSettings.getWidgetLanguageConfig(user)).pipe(
                map((defaultLanguageConfig) => {
                  const isLanguageEmpty =
                    viewPreset?.settings?.language == null;

                  const langConfig = isLanguageEmpty
                    ? defaultLanguageConfig
                    : viewPreset.settings?.language;

                  const editModeConfig = viewPreset.settings?.editMode
                    ? viewPreset.settings.editMode
                    : false;

                  const viewConfig = {
                    language: langConfig,
                    editMode: editModeConfig,
                  };

                  return fetchWidgetSettingsSuccess({
                    defaultLanguageConfig,
                    viewConfig,
                  });
                }),
              ),
            ),
            catchError((error) => of(fetchWidgetSettingsFail({ error }))),
          ),
      ),
    ),
  );

  toggleWidgetConfiguration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(toggleWidgetLanguageOption, toggleWidgetEditMode),
      withLatestFrom(
        this.store.select(selectWidgetLanugageConfig), // PropertyViewConfig[]
        this.store.select(selectCurrentObject),
        this.store.select(selectWidgetEditMode),
      ),
      filter(([_, __, object]) => !!object),
      switchMap(([action, languageConfig, object, editMode]) => {
        const settings: {
          language: PropertyViewConfig[];
          editMode: boolean;
        } = {
          language: (languageConfig ?? []) as PropertyViewConfig[],
          editMode,
        };

        // If this is the language toggle action, update selected flags
        if (action.type === toggleWidgetLanguageOption.type) {
          const a = action as ReturnType<typeof toggleWidgetLanguageOption>;

          settings.language = settings.language.map((item) => ({
            ...item,
            selected: item.label === a.optionName,
          }));
        }

        const viewPreset: ViewPreset = {
          view_id: ViewId.PanelViewWidgets, // verify vs old PanelLanguageWidgets
          name: '',
          settings,
          context_object_id: object.id,
          context_object_class_id: object.object_class_id,
        };

        return this.viewPresetService.saveViewPreset(viewPreset, false).pipe(
          map(() =>
            action.type === toggleWidgetLanguageOption.type
              ? toggleWidgetLanguageOptionSuccess()
              : toggleWidgetEditModeSuccess(),
          ),
          catchError((error) =>
            of(
              action.type === toggleWidgetLanguageOption.type
                ? toggleWidgetLanguageOptionFail({ error })
                : toggleWidgetEditModeFail({ error }),
            ),
          ),
        );
      }),
    ),
  );

  toggleWidgetsEditMode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(toggleWidgetEditMode),
      withLatestFrom(
        this.store.select(selectWidgets),
        this.store.select(selectWidgetEditMode),
      ),

      filter(([_, __, editMode]) => !editMode),

      mergeMap(([_, widgets]) =>
        Array.from(widgets.values())
          .filter(
            (widget) =>
              widget.is_loaded &&
              widget.widget_type_id !== WidgetType.WIDGET_TYPE_PLACEHOLDER,
          )
          .map((widget) => widgetActions.loadWidget({ widgetId: widget.id })),
      ),
    ),
  );

  constructor(
    private store: Store<AppState>,
    private actions$: Actions,
    private coreService: CoreService,
    private userService: UserService,
    private objectService: ObjectService,
    private toastService: ToastService,
    private translateService: TranslateService,
    private router: Router,
    private widgetService: WidgetService,
    private viewPresetService: ViewPresetService,
    private homeSettings: HomeSettingsService,
    private workspaceSettings: WorkspaceSettingsService,
    private widgetSettings: WidgetSettingsService,
  ) {}
}
